import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import { Reveal, SectionHead } from "@/components/Reveal";
import { DutchieEmbed } from "@/components/DutchieEmbed";
import { FaqAccordion } from "@/components/FaqAccordion";
import { ContactActions, HoursList, MapFrame } from "@/components/VisitBlock";
import { BUSINESS, DUTCHIE, FAQS } from "@/lib/site";
import { track } from "@/lib/analytics";

export const TopSellers = () => (
  <section className="on-dark grain relative bg-brand-ink py-20 text-white sm:py-28" data-testid="home-top-sellers">
    <div className="relative mx-auto max-w-7xl px-4 sm:px-6">
      <Reveal className="mb-10 flex flex-wrap items-end justify-between gap-6">
        <SectionHead dark eyebrow="Live from our menu" title="Best Sellers">
          <p className="mt-3 max-w-md text-sm text-neutral-400">Prices and availability update in real time from our Dutchie menu.</p>
        </SectionHead>
        <Link to="/shop-all" onClick={() => track("shop_now_click", { location: "top_sellers" })} data-testid="top-sellers-view-all-button" className="btn-lime">Shop all</Link>
      </Reveal>
      <div className="overflow-hidden rounded-2xl bg-white p-2 sm:p-4" onClickCapture={() => track("product_interaction", { location: "top_sellers" })}>
        <DutchieEmbed scriptId={DUTCHIE.carouselId} src={DUTCHIE.carouselSrc} minHeight={380} testId="dutchie-carousel" label="best sellers" />
      </div>
    </div>
  </section>
);

const STEPS = [
  ["Browse the menu", "Search or filter by category — everything shown is in stock now."],
  ["Order ahead", "Add to cart and check out online to reserve your order."],
  ["Come upstairs", "Find Chicken + Whiskey, take the first door on the left and head up."],
  ["Ring & pick up", "Bring your 21+ ID and medical registration to collect your order."],
];

export const HowToOrder = () => (
  <section className="mx-auto max-w-7xl px-4 py-20 sm:px-6 sm:py-28" data-testid="home-how-it-works">
    <Reveal className="mb-12 max-w-xl"><SectionHead eyebrow="Simple by design" title="How to Order" /></Reveal>
    <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4 lg:gap-6">
      {STEPS.map(([t, d], i) => (
        <Reveal key={t} delay={0.06 * i}>
          <div className="h-full border border-neutral-200 bg-white p-8 transition-[border-color,box-shadow] duration-300 hover:border-brand-ink hover:shadow-[0_8px_24px_rgba(0,0,0,0.08)]" data-testid={`order-step-${i + 1}`}>
            <span className="font-display text-4xl font-black text-brand-lime [-webkit-text-stroke:1px_#121212]">0{i + 1}</span>
            <h3 className="mt-4 font-display text-lg font-bold tracking-tight">{t}</h3>
            <p className="mt-2 text-sm leading-relaxed text-neutral-500">{d}</p>
          </div>
        </Reveal>
      ))}
    </div>
    <div className="mt-6 grid gap-4 md:grid-cols-2" data-testid="medical-card-guide">
      <Reveal className="rounded-2xl bg-brand-lime p-8">
        <p className="text-xs font-bold uppercase tracking-[0.25em]">DC residents</p>
        <h3 className="mt-3 font-display text-2xl font-black tracking-tight">Need a medical card?</h3>
        <p className="mt-2 text-sm leading-relaxed text-brand-ink/80">Adults 21+ can self-certify online through DC's official ABCA portal — no doctor's visit needed.</p>
        <div className="mt-6 flex flex-wrap items-center gap-4">
          <a href={BUSINESS.dcCardUrl} target="_blank" rel="noopener noreferrer" data-testid="dc-card-apply-link" className="btn-dark">Self-certify now</a>
          <Link to="/medical-card" data-testid="home-card-guide-link" className="text-sm font-bold underline underline-offset-4">How it works</Link>
        </div>
      </Reveal>
      <Reveal delay={0.08} className="rounded-2xl bg-brand-sand p-8">
        <p className="eyebrow">Visiting DC</p>
        <h3 className="mt-3 font-display text-2xl font-black tracking-tight">Shopping from out of state?</h3>
        <p className="mt-2 text-sm leading-relaxed text-neutral-600">Visitors can register through ABCA's non-DC resident form, or bring a valid medical cannabis card from their home state.</p>
        <div className="mt-6 flex flex-wrap items-center gap-4">
          <a href={BUSINESS.nonDcCardUrl} target="_blank" rel="noopener noreferrer" data-testid="non-dc-form-link" className="btn-outline">Non-DC form</a>
          <Link to="/medical-card" data-testid="non-dc-faq-link" className="inline-flex items-center gap-1.5 text-sm font-bold text-brand-red">Medical card guide <ArrowRight className="h-4 w-4" /></Link>
        </div>
      </Reveal>
    </div>
  </section>
);

export const InsideStore = () => (
  <section className="on-dark grain relative bg-brand-ink py-20 text-white sm:py-28" data-testid="home-inside">
    <div className="relative mx-auto grid max-w-7xl items-center gap-12 px-4 sm:px-6 lg:grid-cols-2">
      <Reveal>
        <SectionHead dark eyebrow="Inside District Gaslight" title="A bright little shop above 14th Street">
          <p className="mt-4 text-base leading-relaxed text-neutral-300">We're a local, ABCA-licensed medical cannabis dispensary in Logan Circle. Our team helps you find the right flower, pre-rolls, concentrates, edibles and topicals — or skip the wait by ordering ahead.</p>
        </SectionHead>
        <Link to="/about" data-testid="intro-about-link" className="btn-outline-light mt-8">About us</Link>
      </Reveal>
      <Reveal delay={0.1} className="grid grid-cols-2 gap-4">
        <img src="/img/store-3.webp" alt="Illuminated District Gaslight sign on a red wall" loading="lazy" className="col-span-2 aspect-[16/9] w-full rounded-2xl object-cover" />
        <img src="/img/store-2.webp" alt="Display counters and seating inside District Gaslight" loading="lazy" className="aspect-square w-full rounded-2xl object-cover" />
        <img src="/img/stairs-1.webp" alt="Open sign and lime green wall at District Gaslight" loading="lazy" className="aspect-square w-full rounded-2xl object-cover" />
      </Reveal>
    </div>
  </section>
);

export const VisitSection = () => (
  <section className="mx-auto grid max-w-7xl gap-12 px-4 py-20 sm:px-6 sm:py-28 lg:grid-cols-2" data-testid="home-visit">
    <Reveal>
      <SectionHead eyebrow="Visit us" title="Hours & Location">
        <p className="mt-4 text-base text-neutral-600">{BUSINESS.addressLine} · Upstairs next to Chicken + Whiskey</p>
      </SectionHead>
      <div className="mt-8"><HoursList /></div>
      <div className="mt-8"><ContactActions where="home-visit" /></div>
      <Link to="/areas-we-serve" data-testid="home-areas-link" className="mt-6 inline-flex items-center gap-1.5 text-sm font-bold text-brand-red">Directions from your neighborhood <ArrowRight className="h-4 w-4" /></Link>
    </Reveal>
    <Reveal delay={0.1}><MapFrame className="h-[380px] lg:h-full lg:min-h-[480px]" /></Reveal>
  </section>
);

export const FaqTeaser = () => (
  <section className="border-t border-neutral-200 bg-white py-20 sm:py-28" data-testid="home-faq">
    <div className="mx-auto grid max-w-7xl gap-12 px-4 sm:px-6 lg:grid-cols-5">
      <Reveal className="lg:col-span-2">
        <SectionHead eyebrow="Good to know" title="FAQs">
          <p className="mt-4 text-sm leading-relaxed text-neutral-600">Medical cards, ordering, hours and finding the door.</p>
        </SectionHead>
        <Link to="/faq" data-testid="home-faq-all-link" className="mt-6 inline-flex items-center gap-1.5 text-sm font-bold text-brand-red">View all FAQs <ArrowRight className="h-4 w-4" /></Link>
      </Reveal>
      <Reveal delay={0.1} className="lg:col-span-3"><FaqAccordion items={[FAQS[1], FAQS[3], FAQS[5], FAQS[6]]} /></Reveal>
    </div>
  </section>
);
